import { ethers } from "ethers";
import vaultJson from "@abi/vault/eth.json";
import { formatUnits } from "viem";
import { useEffect, useMemo, useState } from "react";
import { useAccount, useReadContracts } from "wagmi";
import { useInterval, useIdle } from "react-use";
import { vaultsContractConfig } from "@config/contractsConfig";
import { usePathname } from "next/navigation";
import { differenceInCalendarDays } from "date-fns";
import useDappChain from "./useDappChain";
import useDappConfig from "./useDappConfig";

const HOURS_TO_ADD = 16;
const DAYS_TO_ADD = 1;

const getClaims = async (
  contract: ethers.Contract,
  walletAddr: string,
  chainId: number
) => {
  const iface = new ethers.utils.Interface(vaultJson);

  const filter = {
    address: vaultsContractConfig[chainId]?.interactionPool.address,
    // Filtering by the event topic directly saves bandwidth and only
    // returns matching logs
    topics: [iface.getEventTopic("WithdrawalRequested")],
  };

  const logs = await contract.queryFilter(
    filter // filtering topic
  );

  // parse logs
  const events = logs.map((log) => iface.parseLog(log));

  const availableRedemptions: Record<
    string,
    Number | Date | string | boolean
  >[] = [];
  events.forEach((ev) => {
    if (!ev?.args) return;
    const [, receiverAddr, , assets, , bnYear, bnMonth, bnDay] = ev.args;

    if (receiverAddr && receiverAddr === walletAddr) {
      const [year, month, day] = [
        bnYear.toNumber(),
        bnMonth.toNumber(),
        bnDay.toNumber(),
      ];

      const str = `${String(bnYear).padStart(2, "0")}-${String(
        bnMonth
      ).padStart(2, "0")}-${String(bnDay).padStart(2, "0")}T00:00:00Z`;

      const claimDate = new Date(str);

      const claimableDateTime =
        claimDate.getTime() +
        (DAYS_TO_ADD * 24 + HOURS_TO_ADD) * 60 * 60 * 1000;
      const updatedclaimableDate = new Date(claimableDateTime);

      availableRedemptions.push({
        isClaimable: Date.now() > updatedclaimableDate.getTime(),
        claimableTime: `~ in ${
          Math.abs(
            differenceInCalendarDays(updatedclaimableDate.getTime(), Date.now())
          ) + 1
        } days`,
        receiver: receiverAddr,
        day,
        month,
        year,
        amount: formatUnits(assets, 18),
        date: claimDate,
        index: availableRedemptions.length + 1,
        key: `${day}-${month}-${year}`,
      });
    }
  });

  const redemptionByDay = availableRedemptions.reduce((prev: any, data) => {
    const key = data.key.toString();
    if (prev[data.key as string]) {
      const upatedAmount = +prev[key].amount + +data.amount;
      prev[key] = {
        ...data,
        amount: upatedAmount.toString(),
      };
    } else {
      prev[key] = {
        ...data,
      };
    }
    return prev;
  }, {});

  return Object.values(redemptionByDay);
};

const POLLING_INTERVAL = 10000;

const provider = new ethers.providers.JsonRpcProvider(
  "https://eth-mainnet.g.alchemy.com/v2/iuphmpG3ftqu_yNyvpDsFtukkzZbyqDK"
  // "https://arbitrum.llamarpc.com"
);

const useVaultClaims = () => {
  const isIdle = useIdle(150e3);
  const { isConnected, address } = useAccount();
  const { chain } = useDappChain();
  const { vaultEnabledChains } = useDappConfig();

  const pathname = usePathname();

  const isGainPage = useMemo(() => {
    return pathname.includes("/airdrop-gain");
  }, []);

  const [cliamList, setClaimList] = useState<any>([]);

  const poolContract = new ethers.Contract(
    vaultsContractConfig[chain.id]
      ? vaultsContractConfig[chain.id]?.interactionPool.address
      : vaultsContractConfig[vaultEnabledChains[0].id].interactionPool.address,
    vaultJson, // ABI
    provider
  );

  const { data: availableClaimBalances, refetch } = useReadContracts({
    contracts: cliamList.map((data: any) => ({
      ...vaultsContractConfig[chain.id]?.interactionPool,
      functionName: "getClaimableAmountByReceiver",
      args: [data.year, data.month, data.day, data.receiver],
    })),
    query: {
      enabled: !isIdle && !!address && cliamList?.length > 0,
    },
  });

  const fetchClaims = async () => {
    if (address) {
      const claims = await getClaims(poolContract, address, chain.id);
      setClaimList(claims || []);
    }
  };

  useEffect(() => {
    refetch();
  }, [cliamList]);

  useEffect(() => {
    if (isConnected && address) {
      fetchClaims();
    }
  }, [isConnected, address]);

  useInterval(() => {
    if (!isIdle && isGainPage) {
      fetchClaims();
    }
  }, POLLING_INTERVAL);

  return cliamList
    .map((data: any, index: number) => {
      return {
        ...data,
        amount:
          availableClaimBalances?.[index]?.result !== undefined
            ? formatUnits(availableClaimBalances[index].result as bigint, 18)
            : data.amount,
      };
    })
    .filter((data: { amount: number }) => data.amount > 0);
};

export default useVaultClaims;
